import { Candle } from "./chartUtils";

export const calculateSMA = (data: Candle[], period: number) => {
  const result: any[] = [];
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) continue;
    let sum = 0;
    for (let j = 0; j < period; j++) sum += data[i - j].close;
    result.push({ time: data[i].time, value: sum / period });
  }
  return result;
};

export const calculateEMA = (data: Candle[], period: number) => {
  if (!Array.isArray(data) || data.length === 0) return [];
  if (!Number.isFinite(period) || period < 1) return [];

  const result: any[] = [];
  const k = 2 / (period + 1);
  let ema = Number(data[0]?.close);
  if (!Number.isFinite(ema)) return [];

  for (let i = 0; i < data.length; i++) {
    const price = Number(data[i]?.close);
    if (!Number.isFinite(price)) continue;
    ema = price * k + ema * (1 - k);
    if (i >= period - 1) result.push({ time: data[i].time, value: ema });
  }
  return result;
};

export const calculateVWAP = (data: Candle[]) => {
  const result: any[] = [];
  let cumVol = 0;
  let cumVolPrice = 0;
  let currentDay: string | null = null;

  for (let i = 0; i < data.length; i++) {
    const d = new Date(data[i].time * 1000).toDateString();
    if (currentDay !== d) {
      cumVol = 0;
      cumVolPrice = 0;
      currentDay = d;
    }
    const typical = (data[i].high + data[i].low + data[i].close) / 3;
    const vol = data[i].volume || 0;
    cumVol += vol;
    cumVolPrice += typical * vol;
    if (cumVol > 0) result.push({ time: data[i].time, value: cumVolPrice / cumVol });
  }
  return result;
};

export const calculateBollingerBands = (data: Candle[], period = 20, multiplier = 2) => {
  const upper: any[] = [];
  const lower: any[] = [];
  const basis = calculateSMA(data, period);
  const offset = period - 1;

  basis.forEach((b: any, idx: number) => {
    const dataIdx = idx + offset;
    let sumSqDiff = 0;
    for (let j = 0; j < period; j++) {
      const val = data[dataIdx - j].close;
      sumSqDiff += Math.pow(val - b.value, 2);
    }
    const stdDev = Math.sqrt(sumSqDiff / period);
    upper.push({ time: b.time, value: b.value + stdDev * multiplier });
    lower.push({ time: b.time, value: b.value - stdDev * multiplier });
  });

  return { upper, lower };
};

export const calculateMACD = (data: Candle[]) => {
  if (!Array.isArray(data) || data.length === 0) {
    return { macd: [], signal: [], histogram: [] };
  }

  const ema12 = calculateEMA(data, 12);
  const ema26 = calculateEMA(data, 26);
  if (ema12.length === 0 || ema26.length === 0) {
    return { macd: [], signal: [], histogram: [] };
  }

  const macdLine: any[] = [];
  const map26 = new Map(ema26.map(i => [i.time, i.value]));

  ema12.forEach(item => {
    const val26 = map26.get(item.time);
    if (val26 !== undefined) {
      macdLine.push({ time: item.time, value: item.value - val26 });
    }
  });

  const k = 2 / (9 + 1);
  const signalLine: any[] = [];
  let ema = macdLine[0]?.value || 0;
  const histogram: any[] = [];

  for (let i = 0; i < macdLine.length; i++) {
    const val = macdLine[i].value;
    ema = val * k + ema * (1 - k);
    if (i >= 8) {
        signalLine.push({ time: macdLine[i].time, value: ema });
        histogram.push({
            time: macdLine[i].time,
            value: val - ema,
            color: (val - ema) >= 0 ? '#26a69a' : '#ef5350'
        });
    }
  }
  return { macd: macdLine, signal: signalLine, histogram };
};

export function toHeikinAshi(candles: Candle[]): Candle[] {
  if (!candles.length) return [];
  const out: Candle[] = [];
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    const prev = out[i - 1];
    const haClose = (c.open + c.high + c.low + c.close) / 4;
    const haOpen = prev ? (prev.open + prev.close) / 2 : (c.open + c.close) / 2;
    const haHigh = Math.max(c.high, haOpen, haClose);
    const haLow = Math.min(c.low, haOpen, haClose);
    out.push({ time: c.time, open: haOpen, high: haHigh, low: haLow, close: haClose, volume: c.volume });
  }
  return out;
}
